import { createTranslator, type useTranslations } from "next-intl"
import english from "@/translations/en/conversation-copy.json"

export type ConversationCopyKey = keyof typeof english.ConversationCopy
export interface ConversationTextCopy {
  key: ConversationCopyKey
  values?: Record<string, string | number | ConversationTextCopy>
}
export type ConversationDisplayCopy = Partial<Record<"label" | "hint" | "confirm" | "text" | "value" | "description" | "reason" | "title" | "contactTitle" | "status", ConversationTextCopy>>
export type ConversationCopyTranslator = ReturnType<typeof useTranslations<"ConversationCopy">>

const englishTranslator = createTranslator({locale: "en", messages: english, namespace: "ConversationCopy"})

/** Resolve only app-owned text. Customer names, classifications and reasons remain interpolation values. */
export function conversationText(copy: ConversationTextCopy, t: ConversationCopyTranslator = englishTranslator): string {
  const values = Object.fromEntries(Object.entries(copy.values ?? {}).map(([key, value]) => [
    key, typeof value === "object" ? conversationText(value, t) : value,
  ]))
  return t(copy.key, values)
}

/** Canonical English stays in the DTO; optional keys make its presentation independent of query-cache locale. */
export function withConversationCopy<const Data extends object, const Copy extends ConversationDisplayCopy>(data: Data, copy: Copy): Data & { [Key in keyof Copy]: string } & { copy: Copy } {
  const text = Object.fromEntries(Object.entries(copy).map(([field, value]) => [field, conversationText(value)]))
  // Keys in the complete copy map are exactly the display fields filled above.
  return {...data, ...text, copy} as Data & { [Key in keyof Copy]: string } & { copy: Copy }
}

/** Translate known display fields without changing IDs, permissions or customer-owned values. */
export function localizeConversationDisplay<Data extends {copy?: ConversationDisplayCopy}>(data: Data, t: ConversationCopyTranslator): Data {
  const fields = Object.fromEntries(Object.entries(data.copy ?? {}).map(([field, copy]) => [field, conversationText(copy, t)]))
  return Object.assign({}, data, fields)
}

/** Source capability declarations derive their canonical text and serialized keys from one map. */
export function canonicalCopyMap<Copy extends Record<string, ConversationTextCopy>>(copy: Copy): {[Key in keyof Copy]: string} {
  // Every entry in the input is resolved, preserving its exact field names.
  return Object.fromEntries(Object.entries(copy).map(([key, value]) => [key, conversationText(value)])) as {[Key in keyof Copy]: string}
}
